import type { FmpExchangeMarketHoliday, FmpExchangeMarketHour } from '@/queries';
import { dayjs } from '@/utilities';

const SESSION_DATE_TIME_FORMAT = 'YYYY-MM-DD hh:mm A';
const MAX_DAYS_TO_SEARCH = 8;

interface MarketSession {
    close: ReturnType<typeof dayjs>;
    open: ReturnType<typeof dayjs>;
}

const stripUtcOffset = (value: string) => value.replace(/\s[+-]\d{2}:\d{2}$/u, '');

const parseSessionTime = (date: string, time: string, timeZone: string) => {
    if (time === 'CLOSED') {
        return undefined;
    }

    const parsed = dayjs.tz(`${date} ${stripUtcOffset(time)}`, SESSION_DATE_TIME_FORMAT, timeZone);

    return parsed.isValid() ? parsed : undefined;
};

const getSessionsForDate = (marketHour: FmpExchangeMarketHour, date: string): MarketSession[] => {
    const ranges = [
        [marketHour.openingHour, marketHour.closingHour],
        [marketHour.openingAdditional, marketHour.closingAdditional],
    ] as const;

    return ranges.flatMap(([opening, closing]) => {
        if (!opening || !closing) {
            return [];
        }

        const open = parseSessionTime(date, opening, marketHour.timezone);
        let close = parseSessionTime(date, closing, marketHour.timezone);

        if (!open || !close) {
            return [];
        }
        if (!close.isAfter(open)) {
            close = close.add(1, 'day');
        }

        return [{ close, open }];
    });
};

const isWeekday = (value: ReturnType<typeof dayjs>) => value.day() !== 0 && value.day() !== 6;

const getClosedHolidayDates = (marketHour: FmpExchangeMarketHour, holidays: readonly FmpExchangeMarketHoliday[]) =>
    new Set(
        holidays
            .filter(
                ({ exchange, isClosed }) =>
                    isClosed && exchange.trim().toUpperCase() === marketHour.exchange.trim().toUpperCase(),
            )
            .map(({ date }) => date),
    );

export const getMarketClosureReason = (
    marketHour: FmpExchangeMarketHour,
    now: Date,
    holidays: readonly FmpExchangeMarketHoliday[] = [],
) => {
    if (marketHour.isMarketOpen) {return undefined;}

    const localNow = dayjs(now).tz(marketHour.timezone);
    const localDate = localNow.format('YYYY-MM-DD');
    const holiday = holidays.find(
        ({ date, exchange, isClosed }) =>
            isClosed &&
            date === localDate &&
            exchange.trim().toUpperCase() === marketHour.exchange.trim().toUpperCase(),
    );

    if (holiday) {return `Closed for ${holiday.name}`;}
    if (!isWeekday(localNow)) {return 'Closed for the Weekend';}
    if (marketHour.openingHour === 'CLOSED' || marketHour.closingHour === 'CLOSED') {return 'Market currently closed';}

    return undefined;
};

/**
 * Finds the next scheduled session boundary in the exchange's local timezone.
 * FMP supplies the live open/closed state; its published session ranges supply
 * the next open/close time, including exchanges with an intraday break.
 */
export const getNextMarketSessionTransition = (
    marketHour: FmpExchangeMarketHour,
    now: Date,
    holidays: readonly FmpExchangeMarketHoliday[] = [],
) => {
    const localNow = dayjs(now).tz(marketHour.timezone);
    const today = localNow.format('YYYY-MM-DD');
    const todaysSessions = getSessionsForDate(marketHour, today);
    const closedHolidayDates = getClosedHolidayDates(marketHour, holidays);

    if (marketHour.isMarketOpen) {
        const nextClose = todaysSessions.find(({ close }) => close.isAfter(localNow))?.close;

        return nextClose?.toDate();
    }

    const nextOpenToday =
        isWeekday(localNow) && !closedHolidayDates.has(today)
            ? todaysSessions.find(({ open }) => open.isAfter(localNow))?.open
            : undefined;

    if (nextOpenToday) {
        return nextOpenToday.toDate();
    }

    for (let dayOffset = 1; dayOffset <= MAX_DAYS_TO_SEARCH; dayOffset += 1) {
        const candidateDay = localNow.add(dayOffset, 'day').startOf('day');

        if (!isWeekday(candidateDay) || closedHolidayDates.has(candidateDay.format('YYYY-MM-DD'))) {
            continue;
        }

        const nextSession = getSessionsForDate(marketHour, candidateDay.format('YYYY-MM-DD'))[0];

        if (nextSession) {
            return nextSession.open.toDate();
        }
    }

    return undefined;
};
